import { useState } from 'react';
import { Check, Zap, ShieldCheck, Sparkles, UserPlus } from 'lucide-react';
import { Button, Card, CardTitle, CardDescription, Input } from '@/components/ui';
import { CollectedCounter } from '@/components/keyword';
import { api } from '@/lib/api';

export function Landing() {
  return (
    <>
      <Hero />
      <section className="py-10 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl">
          <CollectedCounter size="hero" />
        </div>
      </section>
      <FeatureList />
      <CTASection />
    </>
  );
}

// ─── Hero ────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-0 h-[600px] w-[800px] -translate-x-1/2 rounded-full bg-gradient-to-br from-[#FFD70022] via-[#FFA50011] to-transparent blur-3xl" />
      </div>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 pt-16 md:pt-28 pb-16 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
          <Zap className="h-3.5 w-3.5" /> 챌린지 멤버 전용 무료 분석 도구
        </div>
        <h1 className="mt-6 text-4xl sm:text-6xl md:text-7xl font-extrabold tracking-tight leading-tight">
          키워드의 신,&nbsp;
          <span className="text-gold-gradient">KeywordGod</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg md:text-xl text-gray-400">
          6개 경쟁사의 분석력을 하나로. <span className="text-gold">0.05초</span> 안에 답을 드립니다
        </p>

        <div className="mx-auto mt-10 max-w-xl">
          <SignupForm hero />
          <p className="mt-3 text-xs text-gray-500">
            Google로 가입 → 챌린지 운영진 승인 → 모든 기능 무료 사용
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── 가입 폼 (닉네임 + Google) ──────────────────────────────

function SignupForm({ hero = false }: { hero?: boolean }) {
  const [nickname, setNickname] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        api.auth.googleStart(nickname.trim());
      }}
      className={hero ? 'flex flex-col sm:flex-row gap-2' : 'flex flex-col gap-2'}
    >
      <Input
        autoFocus
        maxLength={32}
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
        placeholder="챌린지 닉네임 (운영진이 식별하는 이름)"
        className={hero ? 'h-14 text-lg' : ''}
      />
      <Button
        type="submit"
        variant="gold"
        size={hero ? 'lg' : 'md'}
        className={hero ? 'h-14 shrink-0 text-base' : ''}
        disabled={!nickname.trim()}
      >
        <UserPlus className="h-4 w-4" />
        Google로 가입하기
      </Button>
    </form>
  );
}

// ─── 기능 리스트 ────────────────────────────────────────────

const FEATURES = [
  '0.05초 키워드 분석 (KV → DB → 네이버 API 3단 캐시)',
  '9등급 GodScore + 포화/꿀통/기회 3대 지표',
  '연관 키워드 20개 + 대안 키워드 5개 추천',
  '대량 분석 (한 번에 100개) + CSV 다운로드',
  '키워드 발굴 BFS (depth 2 자동 탐색)',
  '오늘의 황금키워드 자동 갱신',
  '검색 히스토리 + 즐겨찾기',
  '5분마다 시세 자동 업데이트 (tier 기반 우선순위 갱신)',
];

function FeatureList() {
  return (
    <section className="py-12 px-4 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <h2 className="mb-2 text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-gold" /> 챌린지 멤버에게 제공되는 기능
        </h2>
        <p className="mb-6 text-sm text-gray-400">
          모든 기능이 무료. 광고도 결제도 없이 챌린지 멤버 한정으로 운영합니다.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FEATURES.map((f) => (
            <div
              key={f}
              className="flex items-start gap-3 rounded-xl border border-gray-800 bg-gray-900 px-4 py-3 text-sm text-gray-200"
            >
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              {f}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CTA ────────────────────────────────────────────────────

function CTASection() {
  return (
    <section className="py-16 px-4 sm:px-6">
      <Card className="mx-auto max-w-2xl text-center border-gold/40">
        <CardTitle className="text-2xl text-gold-gradient flex items-center justify-center gap-2">
          <ShieldCheck className="h-6 w-6" /> 챌린지 멤버로 시작하세요
        </CardTitle>
        <CardDescription className="mt-2">
          닉네임을 입력하고 Google 계정으로 가입하면 운영진이 누군지 보고 승인합니다.
        </CardDescription>
        <div className="mt-6 max-w-md mx-auto">
          <SignupForm />
        </div>
      </Card>
    </section>
  );
}
